import React from 'react';
import { Button, Card, Row, Col, Typography, Carousel, Statistic, Tag } from 'antd';
import { 
  ArrowRightOutlined, 
  FireOutlined, 
  ClockCircleOutlined, 
  DollarOutlined,
  TagOutlined,
  UserOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import './Home.css';

const { Title, Paragraph } = Typography;

const Home = () => {
  // Sample featured auctions data
  const featuredAuctions = [
    {
      id: 1,
      title: "Vintage Car Collection",
      image: "https://images.unsplash.com/photo-1583121274602-3e2820c69888",
      currentBid: 25000,
      timeLeft: "2d 5h",
      bids: 15,
      category: "Automotive"
    },
    {
      id: 2,
      title: "Rare Art Masterpiece",
      image: "https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5",
      currentBid: 150000,
      timeLeft: "1d 8h",
      bids: 23,
      category: "Art"
    },
    {
      id: 3,
      title: "Luxury Watch Collection",
      image: "https://images.unsplash.com/photo-1524805444758-089113d48a6d",
      currentBid: 45000,
      timeLeft: "3d 12h",
      bids: 18,
      category: "Luxury"
    }
  ];

  // Sample categories
  const categories = [
    { name: "Art", icon: "🎨", count: 156 },
    { name: "Automotive", icon: "🚗", count: 89 },
    { name: "Jewelry", icon: "💎", count: 234 },
    { name: "Electronics", icon: "📱", count: 312 },
    { name: "Collectibles", icon: "🏆", count: 178 },
    { name: "Real Estate", icon: "🏠", count: 45 }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <Title level={1}>Discover Unique Treasures</Title>
          <Paragraph className="hero-subtitle">
            Join the world's most exciting online auction platform. Bid on exclusive items and find your perfect match.
          </Paragraph>
          <div className="hero-search">
            <div className="search-input">
              <SearchOutlined />
              <input type="text" placeholder="Search for items, categories, or sellers..." />
            </div>
            <Button type="primary" size="large">
              Search
            </Button>
          </div>
          <div className="hero-stats">
            <Statistic title="Active Auctions" value={1234} prefix={<FireOutlined />} />
            <Statistic title="Total Users" value={56789} prefix={<UserOutlined />} />
            <Statistic title="Categories" value={24} prefix={<TagOutlined />} />
          </div>
        </div>
      </section>

      {/* Featured Auctions Section */}
      <section className="featured-section">
        <div className="section-header">
          <Title level={2}>Featured Auctions</Title>
          <Link to="/auctions" className="view-all">
            View All <ArrowRightOutlined />
          </Link>
        </div>
        <Row gutter={[24, 24]}>
          {featuredAuctions.map(auction => (
            <Col xs={24} sm={12} lg={8} key={auction.id}>
              <Card
                hoverable
                cover={<img alt={auction.title} src={auction.image} />}
                className="auction-card"
              >
                <Tag color="blue">{auction.category}</Tag>
                <Title level={4}>{auction.title}</Title>
                <div className="auction-details">
                  <div className="bid-info">
                    <DollarOutlined /> Current Bid: ${auction.currentBid.toLocaleString()}
                  </div>
                  <div className="time-info">
                    <ClockCircleOutlined /> {auction.timeLeft} left
                  </div>
                  <div className="bids-info">
                    {auction.bids} bids
                  </div>
                </div>
                <Button type="primary" block>
                  Place Bid
                </Button>
              </Card>
            </Col>
          ))}
        </Row>
      </section>

      {/* Categories Section */}
      <section className="categories-section">
        <div className="section-header">
          <Title level={2}>Browse Categories</Title>
          <Link to="/categories" className="view-all">
            View All <ArrowRightOutlined />
          </Link>
        </div>
        <Row gutter={[24, 24]}>
          {categories.map(category => (
            <Col xs={12} sm={8} lg={4} key={category.name}>
              <Link to={`/categories/${category.name.toLowerCase()}`}>
                <Card hoverable className="category-card">
                  <div className="category-icon">{category.icon}</div>
                  <Title level={4}>{category.name}</Title>
                  <Paragraph>{category.count} items</Paragraph>
                </Card>
              </Link>
            </Col>
          ))}
        </Row>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <Title level={2}>How It Works</Title>
        <Row gutter={[48, 48]} className="steps-container">
          <Col xs={24} md={8}>
            <div className="step-card">
              <div className="step-number">1</div>
              <Title level={3}>Create Account</Title>
              <Paragraph>
                Sign up for free and complete your profile to start bidding on items.
              </Paragraph>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div className="step-card">
              <div className="step-number">2</div>
              <Title level={3}>Browse & Bid</Title>
              <Paragraph>
                Explore thousands of items and place your bids on items you love.
              </Paragraph>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div className="step-card">
              <div className="step-number">3</div>
              <Title level={3}>Win & Collect</Title>
              <Paragraph>
                Win auctions and collect your items. We ensure safe and secure transactions.
              </Paragraph>
            </div>
          </Col>
        </Row>
      </section>

      {/* Call to Action Section */}
      <section className="cta-section">
        <div className="cta-content">
          <Title level={2}>Ready to Start Bidding?</Title>
          <Paragraph>
            Join thousands of users who are already buying and selling on our platform.
          </Paragraph>
          <div className="cta-buttons">
            <Button type="primary" size="large">
              Start Bidding
            </Button>
            <Button size="large">
              Learn More
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home; 